// readinessAdapter — parse the `exportReadiness` reply at the boundary
// (`Packaging.Readiness`). Pure. An unknown finding or shape change reads as
// `unknown` rather than vanishing; a reply without a container id or `ready`
// is malformed.

export { parseExportReadiness };

import { Result } from "@bilda/utils";
import { type ExternalError, mkMalformed } from "./externalError.js";
import type {
    ExportReadiness,
    ReadinessFinding,
    ReadinessPart,
    ReadinessShapeChange,
} from "./ports/brief.js";
import { bool, fields, list, str } from "./wireShape.js";

const partOf = (raw: unknown): readonly ReadinessPart[] => {
    const f = fields(raw);
    const id = str(f?.get("resourceId"));
    const title = str(f?.get("title"));
    return id === undefined ? [] : [{ id, title: title ?? id }];
};

const shapeOf = (raw: unknown): ReadinessShapeChange => {
    const f = fields(raw);
    const type = str(f?.get("type"));
    const id = str(f?.get("resourceId"));
    const title = str(f?.get("title")) ?? id;
    switch (type) {
        case "part_not_in_module":
        case "container_flattened":
            return id === undefined || title === undefined
                ? { type: "unknown" }
                : { type, id, title };
        default:
            return { type: "unknown" };
    }
};

const findingOf = (raw: unknown): ReadinessFinding => {
    const f = fields(raw);
    const type = str(f?.get("type"));
    const target = str(fields(f?.get("target"))?.get("id"));
    switch (type) {
        case "readiness_parts_planned":
        case "readiness_parts_empty":
        case "readiness_parts_removed":
            return { type, parts: list(f?.get("parts")).flatMap(partOf) };
        case "readiness_target_changes":
            return target === undefined
                ? { type: "unknown" }
                : {
                      type,
                      target,
                      losses: list(f?.get("losses")).flatMap(
                          (l) => str(l) ?? [],
                      ),
                      shape: list(f?.get("shape")).map(shapeOf),
                  };
        case "readiness_target_unavailable":
            return target === undefined
                ? { type: "unknown" }
                : { type, target };
        default:
            return { type: "unknown" };
    }
};

const parseExportReadiness = (
    raw: unknown,
): Result.Result<ExportReadiness, ExternalError> => {
    const f = fields(raw);
    const containerId = str(f?.get("containerId"));
    const ready = bool(f?.get("ready"));
    return containerId === undefined || typeof f?.get("ready") !== "boolean"
        ? Result.err(mkMalformed("exportReadiness returned no readiness"))
        : Result.ok({
              containerId,
              ready,
              findings: list(f?.get("findings")).map(findingOf),
          });
};
